/**
 * "What happens next with this record", as the screens read it.
 *
 * Mirrors apps/api/src/modules/crm-clients/lib/nextStep.ts. The three states are
 * decided here rather than shipped on the row, and that is a deliberate
 * difference from how the SLA state travels: an SLA verdict has to match a
 * server-side FILTER exactly, so it is computed once, on the server. A next step
 * is a comparison between one timestamp and the clock, and the clock the
 * operator cares about is the one on their own screen - a page left open since
 * this morning should show a step that fell due at noon as late, without waiting
 * for a reload.
 *
 * The server's filter is deliberately broader than any single state: it matches
 * `none` and `overdue` together, because both are records with nothing planned
 * ahead of them. The row still says which, so the list can be narrowed without
 * the badge becoming vague.
 */

/** `planned` - something ahead. `overdue` - something late. `none` - nothing at all. */
export type NextStepState = "planned" | "overdue" | "none";

/**
 * The state from the earliest outstanding due date.
 *
 * Collapsing `overdue` into `none` is the shortcut this exists to refuse: it
 * would put the "nobody has planned anything" marker on a record that has a plan
 * running three days late, which is a different failure needing a different fix.
 */
export function nextStepState(nextStepAt: string | null | undefined, now = new Date()): NextStepState {
  if (!nextStepAt) return "none";
  return new Date(nextStepAt).getTime() < now.getTime() ? "overdue" : "planned";
}

/** True for the two states that want somebody. */
export function needsNextStep(state: NextStepState): boolean {
  return state !== "planned";
}

/**
 * The CRM tone a state paints with.
 *
 * `planned` is deliberately quiet. A marker on every healthy record is a list
 * where the records that need somebody are the only ones NOT wearing one, which
 * is the opposite of what the marker is for.
 */
export function nextStepTone(state: NextStepState): "wt" | "lt" | "mut" {
  if (state === "overdue") return "lt";
  if (state === "none") return "wt";
  return "mut";
}

/**
 * Whole days between now and a due date, negative once it has passed.
 *
 * Calendar days rather than 24-hour blocks: a step due "tomorrow" should say one
 * day at nine in the morning and at eleven at night, and a difference in
 * milliseconds divided by a day says zero for most of that stretch.
 */
export function daysUntil(nextStepAt: string, now = new Date()): number {
  const due = new Date(nextStepAt);
  const a = Date.UTC(due.getFullYear(), due.getMonth(), due.getDate());
  const b = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((a - b) / 86_400_000);
}

/**
 * A date `n` days from `now` at 09:00 local, as the ISO string the API takes.
 *
 * Nine in the morning rather than the current time of day, because a step
 * planned at 16:47 on Tuesday for "next week" is not due at 16:47 - the operator
 * meant a day, and a time nobody chose reads as precision that is not there.
 *
 * Built from the LOCAL parts and only then converted, never by slicing an ISO
 * string: `new Date(y, m, d, 9)` is nine o'clock where the operator sits, which
 * is the promise being made. Passing a date as `now` with `days: 0` is how a
 * caller says "nine in the morning on THAT day" - the calendar does this with
 * the cell that was clicked.
 */
export function defaultStepAt(days: number, now = new Date()): string {
  return new Date(now.getFullYear(), now.getMonth(), now.getDate() + days, 9, 0, 0, 0).toISOString();
}

/**
 * The earliest outstanding step in a timeline this screen already has.
 *
 * The list routes compute `nextStepAt` server-side; a detail page has the
 * activities themselves and would otherwise have to ask for a number it can
 * already see. Same rule either way: promised (`dueAt`) and not yet carried out
 * (`doneAt`), earliest first, so a promise already broken is not hidden by a plan
 * made for next month.
 */
export function earliestOutstandingAt(
  rows: readonly { dueAt: string | null; doneAt: string | null }[] | undefined,
): string | null {
  let best: string | null = null;
  for (const row of rows ?? []) {
    if (!row.dueAt || row.doneAt) continue;
    if (!best || new Date(row.dueAt).getTime() < new Date(best).getTime()) best = row.dueAt;
  }
  return best;
}
